import { Component, Input, Optional } from '@angular/core';
import {
    ClassNames,
    Color,
    DownloadService,
    FeatureFlagService,
    ITableColumn,
    Size
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqBaseColumnComponent, LsqDataTableComponent } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { PopoverPlacement } from 'ngx-smart-popover';
import { Observable } from 'rxjs';
import { FEE_DESCRIPTION } from '../../../constants/copy.constants';
import {
    ExpenseFeeBreakdown,
    GetAccountOverviewByDateResponse,
    GetFundsOutstandingFeeStatementRequest,
    GetFundsOutstandingFeeStatementService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { FileType } from '@lsq-frontend/remote-services/enterprise/common';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';

@Component({
    selector: 'lsq-fee-popover-column',
    template: `
        <ng-container [cdkColumnDef]="column.key">
            <th
                cdk-header-cell
                *cdkHeaderCellDef
                [ngClass]="[classNames.Sortable, classNames.AlignRight, 'fees-col']"
                [ngStyle]="{ width: column.width || 'auto' }"
                [class.sortable]="column.sortable"
                (click)="changeSort()"
            >
                <div class="th-content">
                    <ng-container *ngIf="column.headerPopoverContent">
                        <popover-content
                            #headerPopover
                            [size]="column.headerPopoverSize || 'small'"
                        >
                            {{ column.headerPopoverContent }}
                        </popover-content>
                        <div
                            [popover]="headerPopover"
                            [popoverPlacement]="getHeaderPopoverPlacement()"
                        >
                            {{ column.header }}
                        </div>
                    </ng-container>

                    <ng-container *ngIf="!column.headerPopoverContent">
                        {{ column.header }}
                    </ng-container>

                    <div *ngIf="isSortable$ | async">
                        <lsq-icon
                            class="sort-caret"
                            [ngClass]="{ 'is-sorted': isCurrentlySorted() }"
                            [type]="getIconType()"
                            [color]="getIconColor()"
                        >
                        </lsq-icon>
                    </div>
                </div>
            </th>
            <td
                cdk-cell
                *cdkCellDef="let row; let index = index"
                [ngClass]="[classNames.AlignRight, 'fees-col', 'popover-pad']"
                [class.sortable]="column.sortable"
                [ngStyle]="{ width: column.width || 'auto' }"
            >
                <a
                    *ngIf="displayFundsOutstandingStatementLink(row.feeBreakdown)"
                    (click)="getFundsOutStandingFeeStatement(row.date)"
                >
                    <span>{{ row.feesIncurred | currencyDash }}</span>
                </a>
                <span *ngIf="!displayFundsOutstandingStatementLink(row.feeBreakdown)">
                    {{ row.feesIncurred | currencyDash }}</span
                >
                <popover-content #feePopover [size]="size.MediumSmall">
                    <table class="expense-popover">
                        <tbody>
                            <tr *ngFor="let fee of row.feeBreakdown.expensesList">
                                <td>{{ fee.category }}</td>
                                <td>
                                    <div>
                                        <lsq-icon
                                            [type]="icons.Add"
                                            [color]="color.Black"
                                        ></lsq-icon>
                                        {{ fee.amount | currencyDash }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td i18n="Fees popover total label">Fees</td>
                                <td>{{ row.feeBreakdown.totalExpenses | currencyDash }}</td>
                            </tr>
                        </tbody>
                    </table>
                </popover-content>
                <lsq-icon
                    *ngIf="displayPopover(row.feeBreakdown)"
                    [type]="icons.Info"
                    [color]="color.Gray"
                    [ngClass]="[classNames.TableCellPopoverIcon, classNames.PopoverIcon]"
                    [popover]="feePopover"
                    [popoverPlacement]="placement.BottomLeft"
                    [popoverSize]="size.MediumSmall"
                >
                </lsq-icon>
            </td>
            <td
                cdk-footer-cell
                *cdkFooterCellDef
                [ngClass]="[classNames.AlignRight, 'fees-col']"
                [class.sortable]="column.sortable"
            >
                <ng-container *ngIf="(data$ | async)?.totalFeeBreakdown">
                    <span>{{ (data$ | async)?.totalFees | currencyDash }}</span>
                    <popover-content #totalFeesPopover [size]="size.Medium">
                        <table class="expense-popover">
                            <tbody>
                                <tr
                                    *ngFor="
                                        let fee of (data$ | async)?.totalFeeBreakdown.expensesList
                                    "
                                >
                                    <td>{{ fee.category }}</td>
                                    <td>
                                        <div>
                                            <lsq-icon
                                                [type]="icons.Add"
                                                [color]="color.Black"
                                            ></lsq-icon>
                                            {{ fee.amount | currencyDash }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td i18n="Fees popover total label">Fees</td>
                                    <td>{{ (data$ | async)?.totalFees | currencyDash }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </popover-content>
                    <lsq-icon
                        *ngIf="
                            (data$ | async)?.totalFees > 0 &&
                            (data$ | async)?.totalFeeBreakdown?.expensesList?.length > 0
                        "
                        [type]="icons.Info"
                        [color]="color.Gray"
                        [ngClass]="[classNames.TableCellPopoverIcon, classNames.PopoverIcon]"
                        [popover]="totalFeesPopover"
                        [popoverPlacement]="placement.TopRight"
                        [popoverSize]="size.Medium"
                    >
                    </lsq-icon>
                </ng-container>
            </td>
        </ng-container>
    `
})
export class FeePopoverColumnComponent extends LsqBaseColumnComponent {
    @Input() public data$: Observable<GetAccountOverviewByDateResponse>;
    @Input() public column: ITableColumn = {
        key: 'feesIncurred',
        header: '',
        headerPopoverContent: FEE_DESCRIPTION,
        sortable: true
    };

    public classNames = ClassNames;
    public placement = PopoverPlacement;
    public icons = Icons;
    public color = Color;
    public size = Size;

    constructor(
        @Optional() public dataTable: LsqDataTableComponent,
        public i18n: I18n,
        private _getFundsOutstandingFeeStatementService: GetFundsOutstandingFeeStatementService,
        private _userService: UserService,
        private _downloadService: DownloadService,
        private _featureFlagService: FeatureFlagService
    ) {
        super(dataTable);
        if (!this.column.header) {
            this.column.header = this.i18n({
                value: 'Fees',
                meaning: 'Fees header label'
            });
        }
    }

    public displayFundsOutstandingStatementLink(feeBreakdown: ExpenseFeeBreakdown): boolean {
        let response = false;
        if (this._featureFlagService.isOnSync(FeatureFlags.FundsOutstandingFeeStatement)) {
            feeBreakdown.expensesList.forEach((x) => {
                if (x.category === 'Funds Usage Daily Fee') {
                    response = true;
                }
            });
        }
        return response;
    }

    public displayPopover(feeBreakdown: ExpenseFeeBreakdown): boolean {
        return feeBreakdown.expensesList && feeBreakdown.expensesList.length > 0;
    }

    public getFundsOutStandingFeeStatement(date): void {
        const request = new GetFundsOutstandingFeeStatementRequest({
            asOfDateFilter: date,
            companyKey: this._userService.getCompanyKey()
        });

        this._downloadService.execute(
            this._getFundsOutstandingFeeStatementService,
            request,
            FileType.Csv,
            `Funds Outstanding Daily Fee Statement ${date}`
        );
    }
}
